import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './store';
import {
  isLegalPath, isExplainerPath, isSignupPath, isAuthFlowPath,
  isPricingPath, isStoresPath, isStatusPath, isStolenRegistryPath,
  isLookupPath, isCardChainPath, isShowFloorEventPath, isShowFloorUserPath,
  isShowFloorMarketingPath, isWalletBouncePath, isMarketplacePath,
  isHelpPath, isAppOrPublicBinderPath, isFeesPath,
} from './paths';
import { Screenshot } from './Screenshot';
import { initAll as initAnimations } from './animations';
import './index.css';

// Code-split every page module — only the matched route ships its
// bundle. The named-export-to-default shim is what React.lazy expects
// (modules with `export const X` need their export hoisted to
// `default` for lazy()'s contract).
const LegalRouter            = lazy(() => import('./LegalPages').then((m) => ({ default: m.LegalRouter })));
const ExplainerRouter        = lazy(() => import('./ExplainerPages').then((m) => ({ default: m.ExplainerRouter })));
const SignupPage             = lazy(() => import('./SignupPage').then((m) => ({ default: m.SignupPage })));
const AuthFlowRouter         = lazy(() => import('./AuthFlowPages').then((m) => ({ default: m.AuthFlowRouter })));
const PricingPage            = lazy(() => import('./PricingPage').then((m) => ({ default: m.PricingPage })));
const StoresPage             = lazy(() => import('./StoresPage').then((m) => ({ default: m.StoresPage })));
const StatusPage             = lazy(() => import('./StatusPage').then((m) => ({ default: m.StatusPage })));
const StolenRegistryPage     = lazy(() => import('./StolenRegistryPage').then((m) => ({ default: m.StolenRegistryPage })));
const LookupPage             = lazy(() => import('./LookupPage').then((m) => ({ default: m.LookupPage })));
const CardChainPage          = lazy(() => import('./CardChainPage').then((m) => ({ default: m.CardChainPage })));
const ShowFloorEventPage     = lazy(() => import('./ShowFloorPages').then((m) => ({ default: m.ShowFloorEventPage })));
const ShowFloorUserPage      = lazy(() => import('./ShowFloorPages').then((m) => ({ default: m.ShowFloorUserPage })));
const ShowFloorMarketingPage = lazy(() => import('./ShowFloorMarketingPage').then((m) => ({ default: m.ShowFloorMarketingPage })));
const WalletBouncePage       = lazy(() => import('./WalletBouncePage').then((m) => ({ default: m.WalletBouncePage })));
const MarketplaceRouter      = lazy(() => import('./MarketplacePages').then((m) => ({ default: m.MarketplaceRouter })));
const HelpRouter             = lazy(() => import('./HelpPages').then((m) => ({ default: m.HelpRouter })));
const AppRouter              = lazy(() => import('./AppRouter')); // default export
const FeeComparisonPage      = lazy(() => import('./FeeComparisonPage'));

// Splash kept dead-simple: full-bleed cream so the lazy chunk swap
// doesn't flash white on a dark-themed sub-page. No spinner — the
// chunk fetches are typically <100ms on a warm connection and the
// flash is more distracting than a blank background.
const RouteSplash = () => (
  <div style={{ minHeight: '100vh', background: 'var(--cream, #f4efe6)' }} aria-hidden="true" />
);

const withSuspense = (Component) => (
  <Suspense fallback={<RouteSplash />}><Component /></Suspense>
);

export default function App() {
  const init = useAuth((s) => s.init);
  const user = useAuth((s) => s.user);
  const isLoading = useAuth((s) => s.isLoading);
  const logout = useAuth((s) => s.logout);
  const [referralCode] = useState(() => new URLSearchParams(window.location.search).get('ref') || '');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { init(); }, [init]);

  // Scroll-triggered reveals + hero parallax. Runs on mount and on
  // client-side route dispatch; the animations module is idempotent.
  useEffect(() => {
    const t = setTimeout(initAnimations, 50);
    return () => clearTimeout(t);
  });

  if (isLoading) return null;

  // Route dispatch — each branch returns a Suspense-wrapped lazy
  // page. Path predicates are imported from ./paths so this file
  // never pulls in the heavy page modules itself.
  if (isLegalPath())              return withSuspense(LegalRouter);
  if (isExplainerPath())          return withSuspense(ExplainerRouter);
  if (isSignupPath())             return withSuspense(SignupPage);
  if (isAuthFlowPath())           return withSuspense(AuthFlowRouter);
  if (isPricingPath())            return withSuspense(PricingPage);
  if (isStoresPath())             return withSuspense(StoresPage);
  if (isStatusPath())             return withSuspense(StatusPage);
  if (isStolenRegistryPath())     return withSuspense(StolenRegistryPage);
  if (isLookupPath())             return withSuspense(LookupPage);
  if (isCardChainPath())          return withSuspense(CardChainPage);
  // ShowFloor marketing MUST come before the :username dispatch so
  // the literal "/show-floor" doesn't get pulled into the username
  // route (it wouldn't match the regex, but order keeps it explicit).
  if (isShowFloorMarketingPath()) return withSuspense(ShowFloorMarketingPage);
  // Stripe Connect Express onboarding return/refresh landing pages.
  // Stripe redirects here over HTTPS; the page auto-bounces to the
  // cardshop:// deep link so the user lands back in the mobile app.
  if (isWalletBouncePath())       return withSuspense(WalletBouncePage);
  if (isShowFloorEventPath())     return withSuspense(ShowFloorEventPage);
  if (isShowFloorUserPath())      return withSuspense(ShowFloorUserPage);
  if (isMarketplacePath())        return withSuspense(MarketplaceRouter);
  if (isHelpPath())               return withSuspense(HelpRouter);
  if (isFeesPath())               return withSuspense(FeeComparisonPage);
  // Logged-in collector tools (/app/*) + public binder pages
  // (/u/:username/binder/:linkToken) — handled by a nested React
  // Router sub-app to keep the marketing site path-based.
  if (isAppOrPublicBinderPath())  return withSuspense(AppRouter);

  return (
    <div>
      <Toaster position="top-center" toastOptions={{ style: { background: 'var(--ink)', color: 'var(--cream)', fontSize: 14 } }} />

      {/* NAV */}
      <nav className="nav">
        <div className="nav-inner">
          <div className="nav-logo">
            <div className="nav-logo-icon">
              <picture>
                <source srcSet="/logo48.webp" type="image/webp" />
                <img src="/logo48.png" alt="" width="22" height="22" />
              </picture>
            </div>
            <div>
              <div className="nav-logo-text">CARD SHOP</div>
              <div className="nav-logo-sub">by Twomiah</div>
            </div>
          </div>
          <button
            className="nav-hamburger"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
          <div className={`nav-actions${menuOpen ? ' open' : ''}`}>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Welcome, {user.display_name || user.username} 👋</span>
                <button className="btn btn-ghost btn-sm" onClick={() => { logout(); }}>Log Out</button>
              </div>
            ) : (
              <>
                <a href="/contests" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Contests</a>
                <a href="/fees" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Fees vs eBay</a>
                <a href="/how-trading-works" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Collectors</a>
                <a href="/show-floor" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Show sellers</a>
                <a href="/stores" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Shops</a>
                <a href="/marketplace" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Marketplace</a>
                <a href="/lookup" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Verify a Card</a>
                <a href="/stolen" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Stolen Registry</a>
                <a href="/pricing" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Pricing</a>
                <a href="https://app.twomiah.com/login" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Sign In</a>
                <a href="/signup" className="btn btn-ink btn-sm" style={{ textDecoration: 'none' }}>Get Started Free</a>
              </>
            )}
          </div>
          {menuOpen && (
            <div className="nav-backdrop" onClick={() => setMenuOpen(false)} />
          )}
        </div>
      </nav>

      {/* Main landmark — wraps every section between <nav> and
          <footer>. PageSpeed flagged "Document does not have a main
          landmark" because there was no <main>. Single per-page,
          screen-reader-skippable. */}
      <main>

      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: 60 }} className="hero-two-col">
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Brand line — Show Floor is the lead pitch (2026-08-04
                  pivot). The chain moved down to the supporting
                  sections; it's the proof point, not the headline. */}
              <div className="hero-eyebrow fade-up">
                <span className="eyebrow-dot" />
                Show Floor by Twomiah Card Shop
              </div>
              <h1 className="hero-headline fade-up delay-1">
                SELL MORE<br />
                <span className="hero-headline-gold">WITH SHOW FLOOR.</span>
              </h1>
              <p className="hero-sub fade-up delay-2">
                Your showcase only holds so much. With Show Floor, buyers shop
                every card you brought from their phone — the binders and boxes
                behind your table included — and walk up to your booth ready to
                buy. Any phone can scan a card at your table. No app needed.
              </p>

              {/* Primary CTA group — the App Store badge is the
                  hero's primary action. Card Shop is a mobile app
                  first; everything else (web account, /how-trading-
                  works explainer) is a secondary path. Old layout
                  had Create-Account, How-Trading-Works, App Store,
                  and Google Play at equal visual weight, which was
                  decision paralysis. */}
              <div className="fade-up delay-3" style={{ display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                <a
                  href="https://apps.apple.com/us/app/twomiah-cardshop/id6763437381"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '14px 22px', borderRadius: 12,
                    background: '#000', color: '#fff', textDecoration: 'none',
                    border: '1px solid rgba(255,255,255,0.18)',
                    fontSize: 14, fontWeight: 700,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                  }}
                >
                  <span style={{ fontSize: 28, lineHeight: 1 }}></span>
                  <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>Download on the</span>
                    <span style={{ fontSize: 18, fontWeight: 800 }}>App Store</span>
                  </span>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.twomiah.cardshop"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '10px 16px', borderRadius: 10,
                    background: 'transparent', color: 'var(--ink)', textDecoration: 'none',
                    border: '1px solid rgba(15,13,8,0.2)',
                    fontSize: 12, fontWeight: 600,
                    opacity: 0.85,
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}>▶</span>
                  <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                    <span style={{ fontSize: 9, opacity: 0.65 }}>Early access on</span>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>Google Play</span>
                  </span>
                </a>
                <a
                  href="/signup"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '10px 16px', borderRadius: 10,
                    background: 'transparent', color: 'var(--ink)',
                    textDecoration: 'none',
                    border: '1px solid var(--ink)',
                    fontSize: 13, fontWeight: 700,
                  }}
                >
                  Create account on the web →
                </a>
              </div>

              {/* Anchor row — smooth-scroll to in-page sections + a
                  cross-link to the stolen registry. Show Floor is the
                  first stop; the chain follows as the proof point. */}
              <div className="fade-up delay-3" style={{ display: 'flex', gap: 18, marginTop: 18, flexWrap: 'wrap', fontSize: 13 }}>
                <a href="#show-floor" style={{ color: 'var(--ink2)', textDecoration: 'none' }}>↓ Show Floor</a>
                <span style={{ color: 'var(--cream3)' }}>·</span>
                <a href="#chain" style={{ color: 'var(--ink2)', textDecoration: 'none' }}>The chain</a>
                <span style={{ color: 'var(--cream3)' }}>·</span>
                <a href="#how" style={{ color: 'var(--ink2)', textDecoration: 'none' }}>How it works</a>
                <span style={{ color: 'var(--cream3)' }}>·</span>
                <a href="#app" style={{ color: 'var(--ink2)', textDecoration: 'none' }}>Get the app</a>
                <span style={{ color: 'var(--cream3)' }}>·</span>
                <a href="/stolen" style={{ color: 'var(--ink2)', textDecoration: 'none' }}>🛡 Stolen registry →</a>
              </div>

              {/* Traction strip — real numbers from the platform.
                  Replaces the "Live on the App Store" one-liner with
                  a three-up of honest signals: catalog depth, catalog
                  coverage, and platform status. No user counts or
                  testimonials yet — those land here once they're real
                  enough to be persuasive. */}
              <div className="fade-up delay-4" style={{
                marginTop: 22,
                display: 'flex',
                gap: 20,
                flexWrap: 'wrap',
                fontSize: 13,
                color: 'var(--ink2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.02em' }}>10M+</span>
                  <span>cards indexed</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.02em' }}>13</span>
                  <span>sports, every year since 1894</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="eyebrow-dot" style={{ background: 'var(--green)' }} />
                  <span>Live on the App Store — free to join</span>
                </div>
              </div>

              {/* No-install pill — addresses the chicken-and-egg
                  objection a sophisticated visitor will raise: "why
                  would I sign up if my counterparty isn't on it?"
                  Answer: scanning a QR sticker on a card requires no
                  account, no install, no signup. The chain works
                  even when the other party hasn't joined. */}
              <div className="fade-up delay-4" style={{
                marginTop: 14,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                borderRadius: 999,
                background: 'rgba(15,13,8,0.06)',
                border: '1px solid rgba(15,13,8,0.12)',
                fontSize: 13,
                color: 'var(--ink2)',
              }}>
                <span style={{ fontSize: 16 }}>📱</span>
                <span>
                  <strong style={{ color: 'var(--ink)' }}>Buyers don't need the app</strong> — anyone at your table can scan a Card Shop QR sticker with a phone camera and see the card, the price, and the chain.
                </span>
              </div>
            </div>

            {/* Hero visual — the full-day-as-vendor reel in a phone
                frame (replaced the Home Hub screenshot, 2026-08-04).
                The reel shows the actual show-floor loop — intake, go
                live, buyers finding cards from the boxes — which sells
                harder than a static hub shot. Autoplays muted; 9/16
                aspect is pinned so the player never renders squat
                before metadata loads. */}
            <div className="card-showcase" style={{ flexShrink: 0 }}>
              <div style={{
                background: '#0a0a0f',
                border: '1px solid rgba(232, 197, 71, 0.25)',
                borderRadius: 32,
                padding: 10,
                width: 320,
                boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                transform: 'rotate(-1.5deg)',
              }}>
                <video
                  autoPlay
                  muted
                  loop
                  controls
                  playsInline
                  preload="auto"
                  style={{ width: '100%', aspectRatio: '9 / 16', objectFit: 'cover', display: 'block', borderRadius: 22, background: '#000' }}
                >
                  <source src="/full-day-as-vendor.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SHOW FLOOR — the lead pitch ("Sell more with Show Floor",
          2026-08-04 pivot). The core argument: the showcase is a
          physical bottleneck on how much inventory a vendor can sell;
          Show Floor removes it because buyers browse EVERYTHING the
          vendor brought from their phone. Full-day vendor reel embeds
          here. Copy mirrors SHOW_FLOOR_ONE_PAGER + upsell screen. */}
      <section id="show-floor" className="section" style={{ scrollMarginTop: 80 }}>
        <div className="container">
          <div className="section-label">Why Show Floor works</div>
          <h2 className="section-title">
            Your table is bigger<br />
            <span style={{ color: 'var(--gold-text)' }}>than your showcase.</span>
          </h2>
          <p className="section-sub" style={{ marginTop: 16, marginBottom: 8, maxWidth: 760 }}>
            Right now you sell whatever fits in the case. Everything else — the
            binders in the backpack, the boxes under the table — sits there all
            weekend because nobody can see it. Show Floor puts your entire
            inventory on every buyer's phone.
          </p>

          {/* Split: full-day-as-vendor reel + the bring-more-cards story */}
          <div className="hero-two-col" style={{ display: 'flex', alignItems: 'center', gap: 48, marginTop: 40 }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, lineHeight: 1.1, letterSpacing: '-0.01em' }}>
                Bring every card you own.
              </div>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink2)', margin: 0 }}>
                Go live with the binders you brought and every card in them is
                browsable and searchable by everyone at the show — not just the
                slabs you had room to display. A buyer three aisles away searches
                "/99 Prizm," finds the card in the box behind you, and walks to
                your table for it.
              </p>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink2)', margin: 0 }}>
                That inventory used to stay home, or ride along and never leave
                the bag. Now it's all for sale, all weekend, without needing an
                inch of case space. More cards on the table means more sales per
                show — that's the whole pitch.
              </p>
              <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink3)', margin: 0, fontStyle: 'italic' }}>
                The reel at the top of the page is a full day at the table run
                through the app — intake to check-out.
              </p>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
            gap: 20,
            marginTop: 40,
          }}>
            {[
              {
                title: "Sell what the case can't hold",
                body: 'Buyers browse every live card you brought — binders, boxes, the tub under the table — priced and ready, no case space required.',
              },
              {
                title: 'A live booth at any show',
                body: 'Pick the binders you brought, set your table number, tap Go Live. Followers get a push with where to find you.',
              },
              {
                title: 'Buyers search the whole floor',
                body: "Anyone at the show can search every live seller's inventory in one place — no flipping through every binder at every booth.",
              },
              {
                title: 'Any phone can scan — no app needed',
                body: 'A QR sticker on every card. Buyers scan with their phone camera and see the card, your price, and its chain of custody.',
              },
              {
                title: 'End the show, one tap',
                body: "Check out and optionally auto-list everything that didn't sell to the marketplace, so your stock keeps working during the week.",
              },
            ].map((f) => (
              <div
                key={f.title}
                style={{
                  background: 'rgba(232, 197, 71, 0.10)',
                  border: '1px solid var(--gold)',
                  borderRadius: 14,
                  padding: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1.15, letterSpacing: '-0.01em' }}>{f.title}</div>
                <div style={{ fontSize: 14, color: 'var(--ink2)', lineHeight: 1.5 }}>{f.body}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 28, flexWrap: 'wrap', alignItems: 'center' }}>
            <a href="/show-floor" className="btn btn-ink" style={{ textDecoration: 'none' }}>See Show Floor →</a>
            <a href="/pricing#show-floor" className="btn btn-ghost" style={{ textDecoration: 'none' }}>$24.99/mo or $249/yr — pricing</a>
          </div>
        </div>
      </section>

      {/* THREE-AUDIENCE SECTION — moved above the chain sections in
          the 2026-08-04 pivot so product/pricing lands before the
          trust story. Show Floor lane leads. Lanes mirror /pricing. */}
      <section className="section">
        <div className="container">
          <div className="section-label">Who Card Shop is for</div>
          <h2 className="section-title">Three audiences,<br />one platform.</h2>
          <p className="section-sub" style={{ marginTop: 16, marginBottom: 8 }}>
            Whether you sell at shows, collect, or run a card shop — the platform
            is built for what you actually do.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: 20,
            marginTop: 40,
          }}>
            {[
              {
                eyebrow: 'For show-floor sellers',
                title: 'Run your booth like a real storefront',
                price: '$24.99/mo or $249/yr',
                features: [
                  'Every card you bring is browsable — not just what fits in the case',
                  'Live booth at any show — pick binders, set table, go live',
                  'Show price set once per card — auto-applies every weekend',
                  'Stock-camera-friendly QR stickers (any phone scans them)',
                  'Buyer search across every live seller at the show',
                  'Optional auto-list of show inventory when the session ends',
                  'Case Mode — per-card show-floor pricing & trade prefs',
                  'Includes everything in Collector Pro',
                ],
                ctaHref: '/pricing#show-floor',
                ctaText: 'See Show Floor details →',
                bg: 'rgba(232, 197, 71, 0.10)',
                highlight: true,
              },
              {
                eyebrow: 'For collectors',
                title: 'Track, trade, and verify your collection',
                price: 'Free → $9.99/mo',
                features: [
                  'Unlimited card collection (free tier)',
                  'Trade Board with private offers',
                  'Marketplace with chain-of-custody on every transfer',
                  'QR scanning + sticker tagging',
                  'Set completion tracking',
                  'Pro: unlimited binders, Deal Radar, live market view, sold-comp research links',
                ],
                ctaHref: '/pricing#collector',
                ctaText: 'See collector tiers →',
                bg: 'var(--cream)',
              },
              {
                eyebrow: 'For card shops',
                title: 'Inventory, POS, and chain-of-custody for your store',
                price: '$79 → $99/store/mo',
                features: [
                  'POS sales log + record-sale form',
                  'Display case management with EOD reconciliation',
                  'Bulk reprice (delta % / set price / multiplier)',
                  'Inter-location transfer requests with full chain-of-custody',
                  'Per-location sale credit + 30-day revenue rollup',
                  'Bulk QR sticker reprint orders',
                  'Multi-Shop ($99/store): cross-location aggregate, dedicated account manager',
                  'Optional Pro Tagging service — we come tag your inventory',
                ],
                ctaHref: '/stores',
                ctaText: 'Stores · learn more →',
                bg: 'var(--cream)',
              },
            ].map((lane) => (
              <div
                key={lane.eyebrow}
                style={{
                  background: lane.bg,
                  border: `1px solid ${lane.highlight ? 'var(--gold)' : 'var(--cream3)'}`,
                  borderRadius: 14,
                  padding: 28,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 2, color: 'var(--gold-text)', textTransform: 'uppercase' }}>
                  {lane.eyebrow}
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, lineHeight: 1.1, letterSpacing: '-0.01em' }}>
                  {lane.title}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink2)' }}>
                  {lane.price}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, lineHeight: 1.4, flex: 1 }}>
                  {lane.features.map((f) => (
                    <li key={f} style={{ paddingLeft: 18, position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: 'var(--gold)' }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href={lane.ctaHref} className="btn btn-outline" style={{ marginTop: 8, textDecoration: 'none' }}>
                  {lane.ctaText}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPETITIVE CoC LANDSCAPE — the supporting proof point under
          the Show Floor lead. Chain-of-custody isn't a Card Shop
          invention; CGC/SGC do it inside the slab and the major vaults
          (Fanatics, eBay, Goldin, PWCC) do it inside their buildings.
          Card Shop's slice is portable owner-held custody for cards in
          the wild. Naming the alternatives honestly is more credible
          than "nobody else does this" and harder to fact-check away. */}
      <section id="chain" className="section" style={{ background: 'var(--ink)', color: 'var(--cream)', scrollMarginTop: 80 }}>
        <div className="container">
          <div className="section-label" style={{ color: 'var(--gold2)' }}>The chain-of-custody landscape</div>
          <h2 className="section-title" style={{ color: 'var(--cream)' }}>
            Three chains.<br />
            <span style={{ color: 'var(--gold2)' }}>Only one travels with you.</span>
          </h2>
          <p className="section-sub" style={{ color: 'rgba(244,239,230,0.72)', maxWidth: 760, marginTop: 16, marginBottom: 40 }}>
            Ownership history isn't a Card Shop invention. The grading companies build
            a chain inside the slab. The major vaults build one inside their buildings.
            Both require the card to live by their rules. Card Shop is the chain you
            keep when the card is in your hand.
          </p>

          {/* Real chain-of-custody screenshot — a 2-link chain showing
              a card that's actually been transferred (@twomiah14 →
              @tradetester). Beats the card-detail surface for this
              section because it shows the chain populated rather than
              the empty "0 transfers" state. */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 40 }}>
            <div style={{
              background: '#0a0a0f',
              border: '1px solid rgba(232, 197, 71, 0.25)',
              borderRadius: 32,
              padding: 10,
              width: 260,
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}>
              <Screenshot
                name="chain"
                alt="Chain of custody — a card's full ownership timeline, signed and timestamped"
                width={480}
                height={1029}
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 22 }}
              />
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: 18,
          }}>
            {[
              {
                who: 'CGC · SGC slabs',
                tag: 'Slab-scoped',
                strength: 'Tamper-evident slab with a documented cert history. Newer slab generations add NFC + tokenized scan records.',
                limit: 'Lives inside the slab. Raw cards, cross-platform sales, and any event after the card cracks out — not part of the record.',
              },
              {
                who: 'Fanatics Collect · eBay Vault · Goldin · PWCC Vault',
                tag: 'Vault-scoped',
                strength: "Full custody chain on every card while it's in their possession. Tied to grading partners, internal marketplace trading, fractional ownership.",
                limit: "The card has to live in the vault. Withdraw it and the chain stops. You're trusting the building, not holding the card.",
              },
              {
                who: 'Card Shop by Twomiah',
                tag: 'Owner-held · portable',
                strength: 'Chain travels with the card across owners, trades, sales, shows, and shipments. Raw or slabbed. In person or by mail. You keep the card; the chain keeps up.',
                limit: "You're the custodian. Photos, videos, transfer signatures, and the public stolen registry verify what the vaults verify by holding the card themselves.",
                ours: true,
              },
            ].map((col) => (
              <div key={col.who} style={{
                background: col.ours ? 'rgba(232,197,71,0.10)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${col.ours ? 'var(--gold)' : 'rgba(232,197,71,0.20)'}`,
                borderLeft: `3px solid ${col.ours ? 'var(--gold-light)' : 'var(--gold)'}`,
                borderRadius: 10,
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 2, color: col.ours ? 'var(--gold-light)' : 'var(--gold2)', textTransform: 'uppercase' }}>
                  {col.tag}
                </div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
                  {col.who}
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1.5, color: 'rgba(244,239,230,0.5)', textTransform: 'uppercase', marginBottom: 6 }}>
                    What it does
                  </div>
                  <p style={{ fontSize: 14, lineHeight: 1.55, color: 'rgba(244,239,230,0.85)', margin: 0 }}>
                    {col.strength}
                  </p>
                </div>
                <div style={{ borderTop: '1px solid rgba(244,239,230,0.12)', paddingTop: 12 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1.5, color: col.ours ? 'var(--gold-light)' : 'rgba(252,165,165,0.85)', textTransform: 'uppercase', marginBottom: 6 }}>
                    {col.ours ? 'The trade-off' : 'The gap'}
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.5, color: 'rgba(244,239,230,0.7)', margin: 0 }}>
                    {col.limit}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p style={{
            marginTop: 28,
            fontSize: 13,
            color: 'rgba(244,239,230,0.6)',
            textAlign: 'center',
            fontStyle: 'italic',
          }}>
            Slabbed and vaulted is the safest chain. Card Shop is the chain for the cards that aren't.
          </p>
        </div>
      </section>

      {/* IN-PERSON MEETUP — concrete proof of the portable-chain
          claim from the CoC landscape above. The vaults literally
          can't do this (the card has to leave their building),
          which is the strongest single argument for owner-held
          custody. Sits right under the landscape so the "we're
          portable" claim has an immediate "here's how" example. */}
      <section className="section" style={{ background: 'var(--cream2)' }}>
        <div className="container">
          <div className="section-label">Trade at the table</div>
          <h2 className="section-title">
            The chain updates the second<br />
            <span style={{ color: 'var(--gold-strong, #b8932e)' }}>you hand over the card.</span>
          </h2>
          <p className="section-sub" style={{ maxWidth: 760, marginTop: 16, marginBottom: 40 }}>
            Most cards don't ship. They change hands at a coffee shop, a card show,
            a local meetup, a friend's kitchen table. Card Shop is the chain-of-custody
            platform built for that moment — no tracking number, no five-day wait,
            no trusting a vault to do the verification work.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
            gap: 18,
          }}>
            {[
              {
                num: '01',
                emoji: '🤝',
                title: 'One tap to skip shipping',
                body: "When you accept a trade offer, choose “Meet in person.” The shipping + tracking UI disappears. The other side gets a push that the trade is now a meetup.",
              },
              {
                num: '02',
                emoji: '📲',
                title: 'Both sides confirm at the swap',
                body: "At the meetup, each party opens the trade and taps “I got my cards.” The chain doesn't move until both sides agree — protects against “I gave you my card and you walked away.”",
              },
              {
                num: '03',
                emoji: '🔗',
                title: 'Ownership transfers right there',
                body: "Second confirm fires the transfer. Both cards reassign, both binders update, and the chain timeline picks up the new owner before you've left the table.",
              },
            ].map((step) => (
              <div key={step.num} style={{
                background: 'var(--cream)',
                border: '1px solid var(--cream3)',
                borderRadius: 14,
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    letterSpacing: 2,
                    color: 'var(--gold-strong, #b8932e)',
                    textTransform: 'uppercase',
                  }}>
                    Step {step.num}
                  </div>
                  <div style={{ fontSize: 22 }}>{step.emoji}</div>
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1.15, letterSpacing: '-0.01em' }}>
                  {step.title}
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                  {step.body}
                </p>
              </div>
            ))}
          </div>

          <p style={{
            marginTop: 28,
            fontSize: 13,
            color: 'var(--ink3)',
            textAlign: 'center',
            fontStyle: 'italic',
          }}>
            Vaults can't do this — the card has to leave their building.
            Card Shop's chain travels because the card never has to.
          </p>
        </div>
      </section>

      {/* MID-PAGE CTA — escape valve for visitors who are already sold
          by this point. The page is long for a reason (novel concept,
          deep explanation) but a convinced-early visitor shouldn't
          have to scroll through every section to find a way to sign
          up. Quiet strip rather than a competing hero. */}
      <section style={{
        background: 'var(--cream2)',
        borderTop: '1px solid var(--cream3)',
        borderBottom: '1px solid var(--cream3)',
        padding: '36px 20px',
      }}>
        <div className="container" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
          flexWrap: 'wrap',
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: 'var(--font-display)',
            fontSize: 22,
            lineHeight: 1.2,
            color: 'var(--ink)',
            letterSpacing: '-0.01em',
          }}>
            Seen enough? Card Shop is free for collectors.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <a
              href="https://apps.apple.com/us/app/twomiah-cardshop/id6763437381"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ink"
              style={{ textDecoration: 'none' }}
            >
              Get the app →
            </a>
            <a
              href={referralCode ? `/signup?ref=${encodeURIComponent(referralCode)}` : '/signup'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '12px 18px', borderRadius: 10,
                background: 'transparent', color: 'var(--ink)',
                textDecoration: 'none',
                border: '1px solid var(--ink)',
                fontSize: 14, fontWeight: 700,
              }}
            >
              Or sign up on the web
            </a>
          </div>
        </div>
      </section>

      {/* MARKETPLACE — second CoC proof point after the meetup
          section. Frames the buy/sell flow as a chain-of-custody
          extension, not a stand-alone marketplace pitch. The four
          claims all anchor back to receipts: only-list-what-you-own
          guards the chain entry, chain-visible-before-purchase is
          buyer transparency, fees are the positioning differentiator
          we earn the right to make (because verified == higher value),
          and receipts-survive-the-sale is the moat vs eBay/TCGplayer
          where the chain dies at "Sold." */}
      <section className="section" style={{ background: 'var(--ink)', color: 'var(--cream)' }}>
        <div className="container">
          <div className="section-label" style={{ color: 'var(--gold2)' }}>The Card Shop marketplace</div>
          <h2 className="section-title" style={{ color: 'var(--cream)' }}>
            Buy a card.<br />
            <span style={{ color: 'var(--gold2)' }}>Inherit the receipts.</span>
          </h2>
          <p className="section-sub" style={{ color: 'rgba(244,239,230,0.72)', maxWidth: 760, marginTop: 16, marginBottom: 40 }}>
            Every listing is tied to a verified owned card — no screenshot uploads,
            no fake certs. When you buy, the card's chain-of-custody comes with it.
            You're not starting the story over; you're picking it up.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: 18,
          }}>
            {[
              {
                num: '01',
                title: 'Sellers can only list what they own',
                body: "Every listing is attached to a registered owned card. No anonymous uploads. No screenshotted certs. The chain has to start before the listing exists.",
              },
              {
                num: '02',
                title: 'The chain is visible before you buy',
                body: "Every listing detail page shows the full owner history, transfer events, cert verifications, and the seller's trust profile. The same information eBay doesn't show — because it doesn't have it.",
              },
              {
                num: '03',
                title: "Fees that don't punish high-value sales",
                body: "$1 + payment processing, capped at 10% on small sales. A $1,000 card costs you ~$33 in fees here vs $100+ on eBay or TCGplayer. The math gets obvious as soon as you list anything over $50.",
              },
              {
                num: '04',
                title: 'The receipts survive the sale',
                body: "On every other marketplace, the chain dies the moment the card sells. On Card Shop, the buyer inherits the full record — and the next time the card is listed, traded, or transferred, the chain picks up your link too.",
              },
            ].map((item) => (
              <div key={item.num} style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(232,197,71,0.20)',
                borderRadius: 10,
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 2, color: 'var(--gold2)', textTransform: 'uppercase' }}>
                  {item.num}
                </div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>
                  {item.title}
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'rgba(244,239,230,0.78)', margin: 0 }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 28, textAlign: 'center' }}>
            <a href="/marketplace" className="btn btn-gold btn-lg" style={{ textDecoration: 'none' }}>
              Browse the marketplace →
            </a>
          </div>

          <p style={{
            marginTop: 24,
            fontSize: 13,
            color: 'rgba(244,239,230,0.6)',
            textAlign: 'center',
            fontStyle: 'italic',
          }}>
            Every sale is a verified link in the card's chain.
            On every other marketplace, the chain stops at "Sold."
          </p>
        </div>
      </section>

      {/* THE HOBBY HAS TRUST GAPS — names three macro problems cold-
          visiting collectors and individual sellers actually feel:
          social-platform impersonation, dispute opacity, and no
          platform of record for ownership. Card Shop's whole moat is
          the answer to those three. Doesn't appear on /stores — store
          owners have a different pain (operations, not trust). */}
      <section className="section" style={{ background: 'var(--ink)', color: 'var(--cream)' }}>
        <div className="container">
          <div className="section-label" style={{ color: 'var(--gold2)' }}>Why this matters now</div>
          <h2 className="section-title" style={{ color: 'var(--cream)' }}>
            The hobby still has<br />
            <span style={{ color: 'var(--gold2)' }}>trust gaps.</span>
          </h2>
          <p className="section-sub" style={{ color: 'rgba(244,239,230,0.72)', maxWidth: 760, marginTop: 16, marginBottom: 32 }}>
            Card collecting has been peer-to-peer for forty years and the trust
            infrastructure hasn't kept up. Impersonation runs free on social
            platforms. Disputes default to whichever party can argue the loudest.
            Once a card changes hands, there's no shared record of who actually
            owned it.
          </p>
          <p className="section-sub" style={{ color: 'rgba(244,239,230,0.72)', maxWidth: 760, marginBottom: 40 }}>
            Honest sellers are getting impersonated, doxxed, and threatened —
            and walking away from the hobby because there's no recourse. We're
            building the recourse.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: 18,
            marginTop: 32,
          }}>
            {[
              {
                num: 'Problem 01',
                title: 'Spoofed identities',
                desc: 'A scammer copies your Facebook profile, lists your cards, takes the money. You spend the next month explaining yourself to police in a state you\'ve never been to.',
                fix: 'Every card on Card Shop resolves to a verified user account. The scanner shows the buyer who actually owns the card — not whoever has Facebook access.',
              },
              {
                num: 'Problem 02',
                title: 'Disputes between strangers go nowhere',
                desc: "Buyer says the card never arrived. Seller swears they shipped it. No shared record, no verifiable trail. Both sides escalate to a payment processor that doesn't want to adjudicate physical-goods disputes between random people, especially for collectibles.",
                fix: 'Pack-out + unpack videos at $200+, signed transfers, transfer-on-default after a seller goes silent. The receipts exist before the disagreement starts.',
              },
              {
                num: 'Problem 03',
                title: 'No proof of ownership',
                desc: 'Stolen cards reappear on eBay weeks later. "I bought this last year" isn\'t a defense. The hobby has no platform of record for who actually owns what.',
                fix: 'Chain-of-custody on every transfer. Public stolen registry with proof-of-acquisition. eBay scanner that flags listings matching reported-stolen cards.',
              },
            ].map((p) => (
              <div key={p.num} style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(232,197,71,0.25)',
                borderLeft: '3px solid var(--gold)',
                borderRadius: 10,
                padding: 24,
              }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 2, color: 'var(--gold2)', textTransform: 'uppercase', marginBottom: 12 }}>
                  {p.num}
                </div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, marginBottom: 10, lineHeight: 1.2 }}>
                  {p.title}
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'rgba(244,239,230,0.7)', marginBottom: 14 }}>
                  {p.desc}
                </p>
                <div style={{ borderTop: '1px solid rgba(244,239,230,0.12)', paddingTop: 12 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1.5, color: 'var(--green-bright)', textTransform: 'uppercase', marginBottom: 6 }}>
                    Card Shop's answer
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.5, color: 'rgba(244,239,230,0.85)', margin: 0 }}>
                    {p.fix}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick link to the public stolen registry — sits below the
              three problems so a worried buyer can act immediately. */}
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <a href="/stolen" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 18px',
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.4)',
              borderRadius: 999,
              color: '#fca5a5',
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              letterSpacing: 0.2,
            }}>
              ⚠ Buying off-platform? Check the public stolen registry →
            </a>
          </div>

          {/* Community pain quotes — anonymized hobby sentiment, not
              fake testimonials. These are the kinds of things people
              actually say in card-collector communities; we don't
              attribute to specific people because we don't have those
              quotes on the record. */}
          <div style={{
            marginTop: 40,
            padding: 28,
            background: 'rgba(232,197,71,0.06)',
            borderLeft: '3px solid var(--gold)',
            borderRadius: 8,
          }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 2, color: 'var(--gold2)', textTransform: 'uppercase', marginBottom: 16 }}>
              What the hobby is saying
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                '"The seller blocked me after the money cleared. There\'s nothing I can do."',
                '"Bought a card at a show. Three months later I find out it was lifted from a shop two states over."',
                '"The Facebook profile that sold me the card didn\'t exist a week later."',
                '"Honest sellers, cover your ass."',
              ].map((q, i) => (
                <p key={i} style={{
                  margin: 0,
                  fontSize: 15,
                  lineHeight: 1.55,
                  color: 'rgba(244,239,230,0.78)',
                  fontStyle: 'italic',
                }}>
                  {q}
                </p>
              ))}
            </div>
            <p style={{ fontSize: 12, color: 'rgba(244,239,230,0.78)', marginTop: 18, marginBottom: 0 }}>
              Composite community sentiment — the kind of post that
              shows up in hobby forums every week. Card Shop's whole job
              is to make these stories rarer.
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER NOTE — promoted to position 2, trimmed. This is the
          most humanizing copy on the page; it earns trust before features. */}
      <section className="section-sm">
        <div className="container-narrow" style={{ textAlign: 'center' }}>
          <div className="section-label">Why This Exists</div>
          <h2 className="section-title" style={{ fontSize: 'clamp(36px, 6vw, 52px)' }}>
            Follow the card.
          </h2>
          <p className="section-sub" style={{ maxWidth: 720, marginLeft: 'auto', marginRight: 'auto', textAlign: 'left' }}>
            I built Card Shop because I wanted real transparency — for the trades,
            the prices, and the cards themselves. Every deal private, every price
            honest, every card traceable. Follow the card.
            <span className="founder-attribution">— Jeremiah, Eau Claire WI</span>
          </p>
        </div>
      </section>

      {/* "Are you on Card Shop?" — the growth thread's emotional
          high point. Minimal by design: the question is the pitch. */}
      <section className="ayocs-section">
        <div className="container">
          <div className="ayocs-inner">
            <h2 className="ayocs-headline">
              Are you on<br />
              <span className="ayocs-gold">Card Shop?</span>
            </h2>
            <p className="ayocs-sub">
              Your trading circle is moving here. Free to join. Takes 2 minutes.
            </p>
            <a href="/signup" className="btn btn-gold btn-lg" style={{ textDecoration: 'none' }}>
              Create My Account →
            </a>
          </div>
        </div>
      </section>

      {/* SCAN ANY CARD — the buyer-side power that prior copy
          only implied. Frames scanning as a *consumer* advantage,
          not just an inventory mechanic, and dramatizes the
          at-show / at-shop scenarios. */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <div className="section-label">Buyer-side scanning</div>
          <h2 className="section-title">
            See who really owns the card<br />
            <span style={{ color: 'var(--gold-strong, #b8932e)' }}>before you reach for your wallet.</span>
          </h2>
          <p className="section-sub" style={{ maxWidth: 760, marginTop: 16, marginBottom: 32 }}>
            Every Card Shop sticker resolves to a public web page that opens in
            your phone's default camera app. No Card Shop login needed, no app
            install. Point, scan, see the card's full record — owner, grade,
            chain-of-custody, asking price. That works whether you're standing in
            front of a shop case, a show table, or a stranger's photo on Instagram.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: 20,
          }}>
            <div style={{
              background: 'var(--cream2)',
              border: '1px solid var(--cream3)',
              borderRadius: 14,
              padding: 24,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>🏪</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
                At a card shop
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                Browse without flagging down staff. Scan the sticker on the case
                glass, see grade, price, and ownership history without anyone
                unlocking anything. Only ask to hold the card when you're already
                serious about buying it.
              </p>
            </div>
            <div style={{
              background: 'var(--cream2)',
              border: '1px solid var(--cream3)',
              borderRadius: 14,
              padding: 24,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>🎟️</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
                At a card show
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                Walking the floor, you can scan any seller's stickered card and
                confirm they actually own it before any cash changes hands.
                On Card Shop, search across every live seller at the show for
                the card you're chasing — find the right table without walking
                every aisle.
              </p>
            </div>
            <div style={{
              background: 'var(--cream2)',
              border: '1px solid var(--cream3)',
              borderRadius: 14,
              padding: 24,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>🔍</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
                On a stranger's listing
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                Someone DMs you a card on Facebook or Instagram. If it has a
                Card Shop QR in the photo, you can verify the seller's account
                and the card's custody trail in one tap — instead of crossing
                fingers and hoping the profile picture is real.
              </p>
            </div>
            <div style={{
              background: 'var(--cream2)',
              border: '1px solid var(--cream3)',
              borderRadius: 14,
              padding: 24,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>📱</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
                Any phone, no install
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                iOS Camera, Android Camera, any QR reader — they all just open
                the link. The buyer never has to download Card Shop to look up a
                card. That's why sellers actually adopt this: there's no friction
                wall to overcome at the table.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTOR PRO — promote the paid tier with the actual
          collector-side value props (Deal Radar, live market,
          binders, set completion). Was implicit in the "Three
          audiences" lane but never had its own spotlight. */}
      <section className="section" style={{ background: 'var(--cream2)' }}>
        <div className="container">
          <div className="section-label">Collector Pro · $9.99/mo</div>
          <h2 className="section-title">
            For the collector who runs their<br />
            <span style={{ color: 'var(--gold-strong, #b8932e)' }}>collection like a portfolio.</span>
          </h2>
          <p className="section-sub" style={{ maxWidth: 760, marginTop: 16, marginBottom: 32 }}>
            Free is enough to track your collection and trade with your circle.
            Pro is for when you want one-tap comp research, alerts the moment a
            want-list card hits eBay, and a binder that doubles as your
            public showcase.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: 20,
          }}>
            <div style={{
              background: 'var(--cream)',
              border: '1px solid var(--cream3)',
              borderRadius: 14,
              padding: 24,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>📡</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
                Deal Radar
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                Build a want list. We watch eBay for listings that match — and
                ping you when one drops below the comp price you set. Catch the
                missed Buy-It-Nows that go to whoever refreshes first.
              </p>
            </div>
            <div style={{
              background: 'var(--cream)',
              border: '1px solid var(--cream3)',
              borderRadius: 14,
              padding: 24,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>📈</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
                One-tap sold comps
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                Every card in your collection links straight to eBay sold and
                130point — what buyers actually paid this week, scoped to your
                exact variant. No more building keyword searches by hand before
                a trade or sale.
              </p>
            </div>
            <div style={{
              background: 'var(--cream)',
              border: '1px solid var(--cream3)',
              borderRadius: 14,
              padding: 24,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>📚</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
                Unlimited binders
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                Free tier caps your binders; Pro lifts the cap entirely. Per-binder
                analytics (value over time, hottest card, oldest acquisition) plus
                a public share link with QR for showing the binder at a meetup.
              </p>
            </div>
            <div style={{
              background: 'var(--cream)',
              border: '1px solid var(--cream3)',
              borderRadius: 14,
              padding: 24,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>✅</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.2 }}>
                Set completion tracking
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', margin: 0 }}>
                Pick a set, see exactly which cards you're missing, and surface
                Trade Board listings or marketplace offers of those exact cards.
                Closes the gap from "I'm chasing this set" to "here's the next card."
              </p>
            </div>
          </div>
          <div style={{ marginTop: 28, textAlign: 'center' }}>
            <a href="/pricing#collector" className="btn btn-ink btn-lg" style={{ textDecoration: 'none' }}>
              See all collector features →
            </a>
          </div>
        </div>
      </section>

      {/* MY LOCAL LCS — sub-brand feature spotlight */}
      <section style={{
        background: 'var(--ink)',
        color: 'var(--cream)',
        padding: '72px 20px',
        borderTop: '1px solid var(--ink2)',
        borderBottom: '1px solid var(--ink2)',
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.1fr 0.9fr',
            gap: 56,
            alignItems: 'center',
          }} className="lcs-two-col">
            <div>
              {/* Mini sub-brand mark */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 14px',
                background: 'rgba(232, 197, 71, 0.12)',
                border: '1px solid rgba(232, 197, 71, 0.3)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 24,
              }}>
                <span style={{ fontSize: 18 }}>📍</span>
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: 'var(--gold-light)',
                }}>
                  Local Shop Prices · Free
                </span>
              </div>

              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(40px, 6vw, 64px)',
                lineHeight: 0.95,
                letterSpacing: '-0.02em',
                color: 'var(--cream)',
                marginBottom: 20,
              }}>
                GasBuddy,<br />
                <span style={{ color: 'var(--gold-light)' }}>for card boxes.</span>
              </h2>

              {/* Early-access framing. The infrastructure works (shop
                  directory + verified-by-3 price aggregator), but the
                  community-posted price data is just starting to land,
                  so we invite seeding instead of claiming coverage. */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 999,
                background: 'rgba(232,197,71,0.12)',
                border: '1px solid rgba(232,197,71,0.35)',
                color: 'var(--gold-light)',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                marginBottom: 16,
              }}>
                Early access
              </div>

              <p style={{
                fontSize: 17,
                lineHeight: 1.6,
                color: 'rgba(244, 239, 230, 0.75)',
                maxWidth: 520,
                marginBottom: 28,
              }}>
                Sealed booster-box prices at card shops in your area —
                crowdsourced from real buyers, not sticker prices. We're
                seeding the price feed now: drop a verified box price for
                your zip and you set the benchmark for everyone who
                searches it next. Three confirmations and the price goes
                live for the area.
              </p>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href="/how-local-prices-work" className="btn btn-gold btn-lg" style={{ textDecoration: 'none' }}>
                  See how it works →
                </a>
                <a href="#signup" className="btn btn-lg" style={{
                  background: 'transparent',
                  color: 'var(--cream)',
                  border: '1px solid rgba(244, 239, 230, 0.3)',
                  textDecoration: 'none',
                }}>
                  Join free
                </a>
              </div>

              {/* Stats line */}
              <div style={{
                display: 'flex',
                gap: 36,
                marginTop: 40,
                paddingTop: 24,
                borderTop: '1px solid rgba(244, 239, 230, 0.1)',
                flexWrap: 'wrap',
              }}>
                {[
                  { val: '100 mi', label: 'Search radius' },
                  { val: 'Pokemon / MTG / YGO / Sports', label: 'Hobbies covered' },
                  { val: '12 wk', label: 'Price trend window' },
                ].map((s) => (
                  <div key={s.label}>
                    <div style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 22,
                      color: 'var(--gold-light)',
                      letterSpacing: '-0.02em',
                    }}>
                      {s.val}
                    </div>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      color: 'rgba(244, 239, 230, 0.55)',
                      marginTop: 2,
                    }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Real My Local LCS screenshot in a phone frame.
                Replaces the hand-drawn shop list that used to mock the
                same surface. */}
            <div style={{ position: 'relative', perspective: '1200px', display: 'flex', justifyContent: 'center' }}>
              <div style={{
                background: '#0a0a0f',
                border: '1px solid rgba(232, 197, 71, 0.25)',
                borderRadius: 32,
                padding: 10,
                width: 280,
                boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
                transform: 'rotate(-1.5deg)',
              }}>
                <Screenshot
                  name="local-lcs"
                  alt="My Local LCS — card shops near you, ranked by distance"
                  width={520}
                  height={1126}
                  style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 22 }}
                />
              </div>
            </div>
          </div>

          <style>{`
            @media (max-width: 820px) {
              .lcs-two-col { grid-template-columns: 1fr !important; gap: 40px !important; }
            }
          `}</style>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section" id="how" style={{ background: 'var(--cream2)', borderTop: '1px solid var(--cream3)', borderBottom: '1px solid var(--cream3)', scrollMarginTop: 80 }}>
        <div className="container">
          <div className="section-label">How It Works</div>
          <h2 className="section-title">Three Steps.<br />That's It.</h2>
          <p className="section-sub">Sign up, list your cards, know what they're worth — and what a box costs near you.</p>

          {/* Trade Board screenshot — the surface step 2 ("List, Offer,
              Trade") actually lands on. Phone-framed and centered
              above the 3-step explainer below. */}
          <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0 36px' }}>
            <div style={{
              background: '#0a0a0f',
              border: '1px solid rgba(232, 197, 71, 0.35)',
              borderRadius: 32,
              padding: 10,
              width: 260,
              boxShadow: '0 20px 50px rgba(15, 13, 8, 0.18)',
            }}>
              <Screenshot
                name="trade-board"
                alt="Trade Board — list cards, find trades, meet up off-platform"
                width={480}
                height={1029}
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 22 }}
              />
            </div>
          </div>

          <div className="steps">
            {[
              { n: '1', emoji: '📱', title: 'Download the app', desc: 'iOS is live on the App Store. Android is in closed testing — request access via the Play Store link below. Free to download, no credit card.' },
              { n: '2', emoji: '🔄', title: 'List, Offer, Trade', desc: "Scan or paste a PSA/BGS cert to autofill. Pack-out and unpack videos required on shipments $200+ — your dispute coverage depends on it. Sellers must ship within 5 days; if they go dark, we transfer the card to you anyway." },
              { n: '3', emoji: '📍', title: 'Check Local Prices', desc: 'Enter your zip to see what a booster box costs at card shops near you — crowdsourced from collectors who actually bought one. Local-price feed is in early access; verified posts go live for the area after three confirmations.' },
            ].map((step, i) => (
              <div key={step.n} className="step">
                {i < 2 && <div className="step-line" />}
                <div className="step-num">{step.n}<div className="step-icon">{step.emoji}</div></div>
                <div className="step-title">{step.title}</div>
                <div className="step-desc">{step.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — answers the obvious questions a cold visitor has
          before they're ready to sign up. Keeps them on the page
          instead of bouncing to "how do I find out X?" Reduces
          cognitive load for a novel concept (chain-of-custody, QR
          stickers, peer-to-peer marketplace) without forcing them
          to read a separate doc. */}
      <section className="section" id="faq" style={{ background: 'var(--cream)', scrollMarginTop: 80 }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <div className="section-label">Common questions</div>
          <h2 className="section-title">The short answers.</h2>
          <p className="section-sub" style={{ marginBottom: 32 }}>
            What people ask before they sign up.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              {
                q: 'How does the chain of custody work?',
                a: "Every card you register gets a unique QR sticker. Every transfer — sale, trade, gift, in-person handoff — is signed by both parties and recorded as a permanent link in that card's chain. Anyone with a phone camera can scan the sticker and see the full history.",
              },
              {
                q: "Do buyers need the app to scan a card?",
                a: "No. Scanning a QR sticker with any phone camera opens a public page with the card's chain — no install, no signup, no account needed. The app is for sellers, traders, and collectors who want to manage their own cards.",
              },
              {
                q: 'Is Card Shop free?',
                a: 'Free for collectors — no credit card, no trial. Optional paid tiers (Collector Pro, Show Floor, Store Starter/Pro) add features like Deal Radar, in-app show booth, and store operations. Marketplace sellers pay a per-sale fee, not a subscription.',
              },
              {
                q: "What's the fee on a marketplace sale?",
                a: '$1 + payment processing, capped at 10% of the card price. A $1,000 card costs around $33 in fees versus $100+ on eBay or TCGplayer.',
              },
              {
                q: "What if a seller takes my money and doesn't ship?",
                a: "Marketplace orders authorize your card on checkout but don't capture (charge) until the seller prints a label or marks shipped. If they go silent past the ship window, the hold drops off your card automatically. If they took payment and ghosted completely, admin review uses the offer + payment receipts on file to transfer ownership of the card to you on the chain — and the card is flagged on the public stolen registry, so anyone scanning the QR sticker sees the stolen banner and the hourly eBay scanner catches it if the seller tries to relist. For peer trades $200+, pack-out and unpack videos are required on both sides for dispute coverage.",
              },
              {
                q: 'What about stolen cards?',
                a: "Cards reported stolen are flagged on the public Stolen Registry — anyone can check a card before buying, app or no app. An hourly scanner sweeps eBay for active listings that match reported-stolen cards; matches go to admin review and then to the cardholder for confirmation.",
              },
              {
                q: 'Do I need QR stickers to use Card Shop?',
                a: "No. The app works without stickers — you can list, trade, sell, and ship through the marketplace using just your account. The QR sticker is a physical anchor for in-person transactions and show-floor displays.",
              },
              {
                q: 'How are graded cards (PSA, BGS, SGC) handled?',
                a: "Slabbed cards register with their cert number, which we cross-check against the grader's lookup. Pair-scan verification adds an in-hand confirmation on top of the slab. Cert + chain together is a stronger ownership-history signal than either alone.",
              },
            ].map((row, i) => (
              <details
                key={i}
                style={{
                  background: 'var(--cream2)',
                  border: '1px solid var(--cream3)',
                  borderRadius: 12,
                  padding: '14px 20px',
                  cursor: 'pointer',
                }}
              >
                <summary style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--ink)',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                }}>
                  <span>{row.q}</span>
                  <span style={{ color: 'var(--gold2)', fontSize: 20, lineHeight: 1, flexShrink: 0 }}>+</span>
                </summary>
                <p style={{
                  marginTop: 12,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: 'var(--ink2)',
                }}>
                  {row.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* APP DOWNLOAD — iOS is publicly live on the App Store as of
          2026-05-12. Android is still in Play Closed Testing while
          we run the 12-tester / 14-day rule. Direct download badge
          for iOS; waitlist-then-internal-track invite for Android.
          Order changed: App Download comes BEFORE the final Signup
          CTA so the page closes on "START FREE. OWN YOUR CARDS."
          energy instead of an Android waitlist caveat. */}
      <div className="container" style={{ padding: '0 20px 0', scrollMarginTop: 80 }} id="app">
        <div className="app-section">
          <div className="app-section-content">
            <div className="app-section-label">Now Live</div>
            <div className="app-section-title">DOWNLOAD<br />CARD SHOP.</div>
            <p className="app-section-sub">
              iOS is live on the App Store — download free and start tracking
              your collection. Android is still in closed testing while Google
              certifies the build; drop your email below and we'll add you to
              the next Play Internal batch.
            </p>
            <div style={{
              display: 'flex', gap: 12, justifyContent: 'center',
              flexWrap: 'wrap', marginTop: 18, marginBottom: 18,
            }}>
              <a
                href="https://apps.apple.com/us/app/twomiah-cardshop/id6763437381"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '14px 22px', borderRadius: 12,
                  background: '#000', color: '#fff', textDecoration: 'none',
                  border: '1px solid rgba(255,255,255,0.18)',
                  minWidth: 200, justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: 32, lineHeight: 1 }}></span>
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'left' }}>
                  <span style={{ fontSize: 11, opacity: 0.75 }}>Download on the</span>
                  <span style={{ fontSize: 19, fontWeight: 700 }}>App Store</span>
                </span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.twomiah.cardshop"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '14px 22px', borderRadius: 12,
                  background: '#000', color: '#fff', textDecoration: 'none',
                  border: '1px solid rgba(255,255,255,0.18)',
                  minWidth: 200, justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: 32, lineHeight: 1 }}>▶</span>
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'left' }}>
                  <span style={{ fontSize: 11, opacity: 0.75 }}>Get it on</span>
                  <span style={{ fontSize: 19, fontWeight: 700 }}>Google Play</span>
                </span>
              </a>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, textAlign: 'center', marginBottom: 8 }}>
              Android isn't publicly downloadable yet — tapping the Play Store badge
              shows an "in testing" page. Use the form below to be added to the
              Play Internal track.
            </p>
            <PlatformWaitlistCapture />
          </div>
        </div>
      </div>

      {/* FINAL SIGNUP CTA — closes the page on energy ("START FREE.
          OWN YOUR CARDS.") instead of the Android-waitlist caveat
          above. Form lives at /signup to keep this surface lean. */}
      <section className="section">
        <div className="free-section">
          <div className="free-section-label">Join Card Shop</div>
          <h2 className="free-section-title">
            START FREE.<br />
            <span className="free-section-gold">OWN YOUR CARDS.</span>
          </h2>
          <p className="free-section-sub">
            Free for collectors — no credit card. Physical QR inserts coming soon.
            {!user && referralCode ? (
              <><br />Invited by <strong>{referralCode}</strong>.</>
            ) : null}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a
              href={referralCode ? `/signup?ref=${encodeURIComponent(referralCode)}` : '/signup'}
              className="btn btn-gold btn-lg"
              style={{ textDecoration: 'none' }}
            >
              Create My Account →
            </a>
          </div>
        </div>
      </section>

      </main>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-copy">© {new Date().getFullYear()} Twomiah LLC · Card Shop by Twomiah</div>
          <div className="footer-links">
            <a className="footer-link" href="/how-trading-works">How Trading Works</a>
            <a className="footer-link" href="/how-local-prices-work">Local Shop Prices</a>
            <a className="footer-link" href="/stolen">Stolen Registry</a>
            <a className="footer-link" href="/terms">Terms</a>
            <a className="footer-link" href="/privacy">Privacy</a>
            <a className="footer-link" href="/dmca">DMCA</a>
            <a className="footer-link" href="/minor-safety">Minor Safety</a>
            <a className="footer-link" href="/paid-features">Paid Features</a>
            <a className="footer-link" href="/law-enforcement">Law Enforcement</a>
            <a className="footer-link" href="/status">Status</a>
            <a className="footer-link" href="mailto:support@twomiah.com">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Unified platform-waitlist capture — both iOS (TestFlight) and
// Android (Play Internal Testing) are invite-only during the closed
// beta. One form per platform, both POSTing to /api/waitlist with
// different `feature` values; we add the email to the relevant
// tester list manually for now.
//
// Replaces the old AndroidInstallBadge (which exposed an open APK
// link) — that pattern broke every new build and didn't match the
// closed-testing access model Google requires.
function PlatformWaitlistCapture() {
  // iOS shipped publicly to the App Store on 2026-05-12, so no
  // TestFlight waitlist form anymore — anyone can install. Android
  // is still gated by Play Closed Testing; the form below adds the
  // email to that tester list manually.
  return (
    <div style={{
      marginTop: 8, maxWidth: 520, marginInline: 'auto',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <PlatformWaitlistForm
        platform="android"
        feature="android_internal"
        icon="🤖"
        title="Android — Play Internal Testing"
        sub="We add you to the Play Internal track and you install via Play Store with auto-updates."
      />
    </div>
  );
}

function PlatformWaitlistForm({ platform, feature, icon, title, sub }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState('');
  const apiBase =
    (typeof window !== 'undefined' && window.location.hostname.includes('localhost'))
      ? 'http://localhost:5000'
      : 'https://cardshop-api-ow4q.onrender.com';

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrMsg('Please enter a valid email');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setErrMsg('');
    try {
      const res = await fetch(`${apiBase}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, feature, source: 'landing' }),
      });
      if (!res.ok) throw new Error(await res.text());
      setStatus('done');
    } catch (err) {
      setErrMsg('Something went wrong — try again in a moment.');
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div style={{
        padding: 18, borderRadius: 10,
        background: 'rgba(232,197,71,0.12)', border: '1px solid rgba(232,197,71,0.35)',
      }}>
        <div style={{ color: 'var(--gold,#e8c547)', fontWeight: 700, marginBottom: 4 }}>
          {icon} You're on the {platform === 'ios' ? 'iOS' : 'Android'} list
        </div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
          We'll email <strong>{email}</strong> when an invite goes out.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: 16, borderRadius: 10,
      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
    }}>
      <div style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, marginBottom: 4 }}>
        {icon} {title}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginBottom: 12 }}>
        {sub}
      </div>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
          required
          style={{
            flex: '1 1 220px', minWidth: 0,
            padding: '10px 12px', borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(0,0,0,0.3)',
            color: '#fff', fontSize: 14,
          }}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="btn btn-ink btn-sm"
          style={{ minWidth: 120 }}
        >
          {status === 'loading' ? 'Adding…' : 'Notify me'}
        </button>
      </form>
      {errMsg ? (
        <div style={{ color: '#ff6b6b', fontSize: 12, marginTop: 8 }}>{errMsg}</div>
      ) : null}
    </div>
  );
}
